import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertTriangle, Mail } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const PARAGRAPHS = [
  "The information and products provided on this website are intended for laboratory research purposes only. They are not intended for human or veterinary use, including but not limited to therapeutic, diagnostic, cosmetic, injectable, ingestible, or recreational applications.",
  "The products offered by EndaG1 have not been evaluated or approved by Health Canada, the U.S. Food and Drug Administration (FDA), or any other regulatory authority for use in humans or animals. Any information presented on this website, including product descriptions, specifications, research summaries, or scientific references, is provided solely for educational and research reference purposes and should not be interpreted as medical advice, treatment recommendations, or efficacy claims.",
  "By purchasing from EndaG1, the purchaser represents and warrants that they are a qualified researcher, laboratory, educational institution, or other authorized entity with the knowledge and expertise required to safely handle research materials. The purchaser assumes all responsibility for the safe handling, storage, use, and disposal of these products in accordance with all applicable federal, provincial/state, and local laws and regulations.",
  "EndaG1 makes no representations or warranties, express or implied, regarding the performance, safety, purity, merchantability, fitness for a particular purpose, or results obtained from the use of its products. Under no circumstances shall EndaG1 be liable for any direct, indirect, incidental, consequential, or special damages arising from the purchase, handling, misuse, or application of any product.",
  "By accessing this website or placing an order, you acknowledge that you have read, understood, and agree to this disclaimer and all applicable Terms and Conditions.",
];

export default function Disclaimer() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Disclaimer"
        description="EndaG1 Disclaimer — all products are strictly for laboratory research use only. Not approved for human or veterinary use."
        canonicalPath="/disclaimer"
      />
      <section className="mx-auto max-w-[900px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-8" data-testid="disclaimer-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#4ADE80]">Legal</p>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5] leading-tight">Disclaimer</h1>
        </motion.div>

        {/* Callout banner */}
        <div className="mt-12 border border-[#4ADE80]/30 bg-[#4ADE80]/[0.04] p-6 sm:p-7 flex items-start gap-4" data-testid="disclaimer-callout">
          <AlertTriangle className="h-6 w-6 text-[#4ADE80] shrink-0 mt-1" strokeWidth={1.5} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#4ADE80]">For Laboratory Research Only</div>
            <p className="mt-2 text-[15px] leading-[1.7] text-[#F5F5F5]">Not for human or veterinary use. Not approved by Health Canada, FDA, or any other regulatory authority for therapeutic use.</p>
          </div>
        </div>

        {/* Paragraphs */}
        <div className="mt-14 space-y-6" data-testid="disclaimer-body">
          {PARAGRAPHS.map((p, i) => (
            <p key={i} className="text-[15px] sm:text-[16px] leading-[1.8] text-[#C9C9C9]">{p}</p>
          ))}
        </div>

        {/* Related links */}
        <div className="mt-16 pt-10 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-4">Related Policies</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/terms" data-testid="disclaimer-link-terms" className="group border border-white/10 bg-[#171717] p-5 hover:border-[#4ADE80]/40 transition-colors">
              <div className="font-display text-lg text-[#F5F5F5] group-hover:text-[#4ADE80] transition-colors">Terms of Service</div>
              <div className="text-xs text-[#A8A8A8] mt-1">Complete terms governing use of our website and products</div>
            </Link>
            <Link to="/privacy" data-testid="disclaimer-link-privacy" className="group border border-white/10 bg-[#171717] p-5 hover:border-[#4ADE80]/40 transition-colors">
              <div className="font-display text-lg text-[#F5F5F5] group-hover:text-[#4ADE80] transition-colors">Privacy Statement</div>
              <div className="text-xs text-[#A8A8A8] mt-1">How we collect, use, and protect Personal Information</div>
            </Link>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-4">Questions?</div>
          <div className="border border-white/10 bg-[#171717] p-6 max-w-md">
            <div className="font-display text-lg text-[#F5F5F5]">EndaG1 Inc.</div>
            <Link to="/#contact" data-testid="disclaimer-contact-form-link" className="mt-4 inline-flex items-center gap-2 bg-[#4ADE80] text-[#0F0F10] px-5 py-2.5 uppercase tracking-[0.14em] text-[11px] hover:bg-[#6FE998] transition-colors">
              <Mail className="h-4 w-4" /> Contact Us
            </Link>
            <a href="mailto:info@endag1.com" className="mt-3 block text-sm text-[#7a7a7a] hover:text-[#4ADE80] transition-colors" data-testid="disclaimer-contact-email">info@endag1.com</a>
          </div>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
